/**
 * Data Migration Runner — background task that executes pending migrations.
 *
 * Flow per migration:
 *   1. Check `db.setting` for completion flag → skip if no `needsMigration()`
 *   2. Always verify with `needsMigration()` (new legacy data may have synced)
 *   3. If needed: try pre-sync, then execute(), then write flag
 *   4. On error: log and continue to next migration (retry on next app start)
 *
 * Designed to be called from BackgroundTaskRunner with priority 3.
 */

import { db } from "@/db/schema"
import { dataMigrations } from "@/background/migrations"
import type { MigrationRecord } from "@/background/migrations/migrationTypes"

// ---------------------------------------------------------------------------
// Setting key helpers
// ---------------------------------------------------------------------------

/**
 * `setting` is a synced table whose primary keys are global in Dexie Cloud: a plain key belongs to the
 * first account that ever synced it. The markers used to be `migration:<id>`, so for every other account
 * the server refused the row ("duplicate key value violates unique constraint objects_pkey"), rolled it
 * back locally, and every migration ran again at every start (seen 2026-10-15 on new persona accounts).
 * A `#` key is private per user (like `#populated`, `#focus:`, `#pin:`).
 */
function settingKey(migrationId: string): string {
  return `#migration:${migrationId}`
}

/** The old global key: still honoured where it did stick (the one account that owns it). */
function legacySettingKey(migrationId: string): string {
  return `migration:${migrationId}`
}

async function getMigrationRecord(
  migrationId: string,
): Promise<MigrationRecord | undefined> {
  const setting =
    (await db.setting.get(settingKey(migrationId))) ?? (await db.setting.get(legacySettingKey(migrationId)))
  if (!setting?.value) return undefined
  try {
    return JSON.parse(setting.value) as MigrationRecord
  } catch {
    return undefined
  }
}

async function setMigrationRecord(record: MigrationRecord): Promise<void> {
  const timestamp = new Date().toISOString()
  await db.setting.put({
    id: settingKey(record.id),
    value: JSON.stringify(record),
    device: "",
    createdAt: timestamp,
    createdBy: "system",
    updatedAt: timestamp,
    updatedBy: "system",
  })
}

// ---------------------------------------------------------------------------
// Pre-migration sync (best-effort)
// ---------------------------------------------------------------------------

async function trySyncBeforeMigration(): Promise<void> {
  try {
    if (db.cloud?.sync) {
      await Promise.race([
        db.cloud.sync(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("sync timeout")), 10_000),
        ),
      ])
    }
  } catch {
    // Sync failed or timed out — continue anyway
    console.debug("[migration-runner] Pre-migration sync skipped or timed out")
  }
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

/**
 * Run all pending data migrations.
 *
 * @returns Total number of records migrated across all migrations.
 */
export async function runDataMigrations(): Promise<number> {
  let totalMigrated = 0

  // The pre-migration sync is only worth its round-trip when a migration is
  // about to write. It used to run unconditionally, so every cold start paid
  // a full db.cloud.sync() (up to 10s) that competed with the user's first
  // writes even though, in practice, every migration is already complete.
  let hasSynced = false

  for (const migration of dataMigrations) {
    try {
      // Check if already completed
      const record = await getMigrationRecord(migration.id)

      // Even if flagged complete, re-verify — new legacy data may have synced
      let needed = await migration.needsMigration()
      if (needed && !hasSynced) {
        // Best-effort sync to get latest data before migrating, then re-check:
        // the sync may have brought in the already-migrated version.
        hasSynced = true
        await trySyncBeforeMigration()
        needed = await migration.needsMigration()
      }
      if (!needed) {
        if (!record) {
          // First time seeing this migration and it's not needed — mark as complete
          await setMigrationRecord({
            id: migration.id,
            completedAt: Date.now(),
            migratedCount: 0,
          })
        }
        continue
      }

      console.info(
        `[migration-runner] Running: ${migration.id} — ${migration.description}`,
      )

      const count = await migration.execute()
      totalMigrated += count

      // Record completion
      await setMigrationRecord({
        id: migration.id,
        completedAt: Date.now(),
        migratedCount: count,
      })

      console.info(
        `[migration-runner] Completed: ${migration.id} — ${count} records migrated`,
      )
    } catch (error) {
      // Log and continue — failed migration retries on next app start
      console.error(
        `[migration-runner] Failed: ${migration.id}`,
        error instanceof Error ? error.message : String(error),
      )
    }
  }

  return totalMigrated
}
